import { useCallback, useRef, useState } from 'react'

import type { AIGenerationState } from '@/hooks/types/aiFieldGeneration'
import { useGlobalState } from '@/hooks/useGlobalState'

import { logAIGenerationError, pollForGenerationResult } from './services/aiGenerationService'

interface UseGeneralInfoAIFieldOptions {
  contentId: string
  systemPromptTemplateId: string
  value: string
  setValue: (v: string) => void
  /** Optional transform applied to the raw AI response before storing as suggestion. */
  postProcess?: (raw: string) => string
}

/**
 * Manages a single AI-assisted field's generate/accept/reject lifecycle
 * for General Information content. Uses refs internally to avoid stale
 * closures in async generation callbacks.
 */
export function useGeneralInfoAIField(options: UseGeneralInfoAIFieldOptions) {
  const { api } = useGlobalState()

  const [aiState, setAiState] = useState<AIGenerationState>('idle')
  const [suggestion, setSuggestion] = useState<string | null>(null)
  const previousValueRef = useRef('')
  const abortControllerRef = useRef<AbortController | null>(null)

  const valueRef = useRef(options.value)
  valueRef.current = options.value

  const aiStateRef = useRef(aiState)
  aiStateRef.current = aiState

  const suggestionRef = useRef(suggestion)
  suggestionRef.current = suggestion

  const contentIdRef = useRef(options.contentId)
  contentIdRef.current = options.contentId

  const systemPromptRef = useRef(options.systemPromptTemplateId)
  systemPromptRef.current = options.systemPromptTemplateId

  const postProcessRef = useRef(options.postProcess)
  postProcessRef.current = options.postProcess

  const setValueRef = useRef(options.setValue)
  setValueRef.current = options.setValue

  const applyPostProcess = useCallback((raw: string): string => {
    const trimmed = raw.trim()
    return postProcessRef.current ? postProcessRef.current(trimmed) : trimmed
  }, [])

  const handleNetworkErrorWithPolling = useCallback(
    async (abortController: AbortController) => {
      const pollResult = await pollForGenerationResult(
        api,
        {
          entityType: 'content',
          entityId: contentIdRef.current,
          systemPromptTemplateId: systemPromptRef.current,
        },
        { signal: abortController.signal },
      )
      if (pollResult.status === 'done') {
        previousValueRef.current = valueRef.current
        setSuggestion(applyPostProcess(pollResult.result))
        setAiState('pending')
      } else if (pollResult.status === 'aborted') {
        // Cancelled by user — state already reset by cancel()
      } else {
        const reason = pollResult.status === 'failed' ? pollResult.error : 'Polling timed out'
        logAIGenerationError(reason, null)
        setAiState('idle')
      }
    },
    [api, applyPostProcess],
  )

  const generate = useCallback(async () => {
    const abortController = new AbortController()
    abortControllerRef.current = abortController
    setAiState('loading')
    try {
      const response = await api.safeRequest(api.generate, {
        content_id: contentIdRef.current,
        system_prompt_template_id: systemPromptRef.current,
        message_template_id: 'generate_step_message',
        extra_data: { previous_value: valueRef.current || undefined },
      })
      if (abortController.signal.aborted) return

      if (response?.ok && response.data) {
        previousValueRef.current = valueRef.current
        setSuggestion(applyPostProcess(response.data as string))
        setAiState('pending')
      } else if (!response.ok && response.isNetworkError) {
        await handleNetworkErrorWithPolling(abortController)
      } else {
        setAiState('idle')
      }
    } catch {
      setAiState('idle')
    } finally {
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null
      }
    }
  }, [api, applyPostProcess, handleNetworkErrorWithPolling])

  const generateWithGuide = useCallback(
    async (instructions: string) => {
      const abortController = new AbortController()
      abortControllerRef.current = abortController
      setAiState('loading')
      try {
        const extraData: Record<string, string | undefined> = {
          previous_value: valueRef.current || undefined,
          user_instructions: instructions.trim() || undefined,
        }
        if (aiStateRef.current === 'pending' && suggestionRef.current) {
          extraData.proposed_data = suggestionRef.current
        }
        const response = await api.safeRequest(api.generate, {
          content_id: contentIdRef.current,
          system_prompt_template_id: systemPromptRef.current,
          message_template_id: 'generate_step_message',
          extra_data: extraData,
        })
        if (abortController.signal.aborted) return

        if (response?.ok && response.data) {
          previousValueRef.current = valueRef.current
          setSuggestion(applyPostProcess(response.data as string))
          setAiState('pending')
        } else if (!response.ok && response.isNetworkError) {
          await handleNetworkErrorWithPolling(abortController)
        } else {
          setAiState('idle')
        }
      } catch {
        setAiState('idle')
      } finally {
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null
        }
      }
    },
    [api, applyPostProcess, handleNetworkErrorWithPolling],
  )

  const accept = useCallback(() => {
    if (suggestionRef.current) {
      setValueRef.current(suggestionRef.current)
      setSuggestion(null)
      setAiState('idle')
    }
  }, [])

  const reject = useCallback(() => {
    setValueRef.current(previousValueRef.current)
    setSuggestion(null)
    setAiState('idle')
  }, [])

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    setSuggestion(null)
    setAiState('idle')
  }, [])

  /** Seed an AI suggestion from server data (e.g. a pre-generated `titleAi` value). */
  const seedSuggestion = useCallback((value: string) => {
    previousValueRef.current = ''
    setSuggestion(value)
    setAiState('pending')
  }, [])

  const reset = useCallback(() => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    setAiState('idle')
    setSuggestion(null)
    previousValueRef.current = ''
  }, [])

  return {
    fieldState: { state: aiState, suggestion } as const,
    generate,
    generateWithGuide,
    accept,
    reject,
    cancel,
    seedSuggestion,
    reset,
  }
}
